import { type RecipientOutsideDataController } from "./DataControllerWarningBanners.js";

/**
 * Minimal representation of a data sharing partnership for checking
 * whether two data controllers share a partnership.
 */
export interface DataSharingPartnershipSummary {
  dataControllers: { id: string }[];
}

/**
 * A recipient candidate with the fields needed to determine whether
 * they are outside the user's data controller.
 */
export interface RecipientCandidate {
  teamType?: string;
  teamName?: string;
  name: string;
  dataControllerId?: string | null;
  dataControllerName?: string | null;
}

/**
 * Returns true if two data controller IDs appear together in the
 * `dataControllers` array of at least one data sharing partnership.
 *
 * Both IDs must be present in the *same* single partnership — transitive
 * relationships through a third data controller do not count.
 */
export function areDataControllersInSamePartnership(
  dataControllerIdA: string,
  dataControllerIdB: string,
  partnerships: DataSharingPartnershipSummary[],
): boolean {
  return partnerships.some(
    (partnership) =>
      partnership.dataControllers.some((dc) => dc.id === dataControllerIdA) &&
      partnership.dataControllers.some((dc) => dc.id === dataControllerIdB),
  );
}

/**
 * Pure function that determines which recipients are outside the user's
 * data controller, taking data sharing partnerships into account.
 *
 * A recipient is considered "outside" when:
 * 1. They are not in a guest or independent context
 * 2. Both the user and the recipient have data controller IDs
 * 3. Their data controller IDs differ
 * 4. Their data controllers do NOT share a data sharing partnership
 *
 * The result is deduplicated by recipientName + recipientDataControllerName.
 */
export function getRecipientsOutsideDataController({
  userDataControllerId,
  currentSelection,
  recipients,
  dataSharingPartnerships,
}: {
  /** The current user's data controller ID */
  userDataControllerId: string | null | undefined;
  /** The currently selected (not yet added) recipient, if applicable */
  currentSelection?: RecipientCandidate | null;
  /** Already-added recipients in the list */
  recipients: RecipientCandidate[];
  /** All known data sharing partnerships */
  dataSharingPartnerships: DataSharingPartnershipSummary[];
}): RecipientOutsideDataController[] {
  const isOutside = (candidate: RecipientCandidate): boolean => {
    // Skip guest or independent contexts
    if (
      candidate.teamType === "guest" ||
      candidate.teamType === "independent"
    ) {
      return false;
    }

    // Skip if no user data controller
    if (!userDataControllerId) return false;

    // Skip if no recipient data controller
    if (!candidate.dataControllerId) return false;

    // Same data controller — not outside
    if (userDataControllerId === candidate.dataControllerId) return false;

    // Check if the two data controllers share a partnership
    if (
      areDataControllersInSamePartnership(
        userDataControllerId,
        candidate.dataControllerId,
        dataSharingPartnerships,
      )
    ) {
      return false;
    }

    return true;
  };

  const allCandidates: RecipientCandidate[] = [
    ...(currentSelection ? [currentSelection] : []),
    ...recipients,
  ];

  return allCandidates.filter(isOutside).map((candidate) => ({
    recipientName: candidate.teamName ?? candidate.name,
    recipientDataControllerName: candidate.dataControllerName,
  }));
}
